import type { JobLogCountResponse, JobLogDataResponse } from './types';

/**
 * Pull the message out of a failed response.
 *
 * The server sends `{"error": "..."}` on failure - see
 * `build_json_error_response` - but a 500 from a layer above the handler can
 * still arrive as HTML, so fall back to the status line.
 */
async function errorMessage(response: Response): Promise<string> {
  let message = `${response.status} ${response.statusText}`;

  try {
    const body = await response.json();
    if (body?.error) message = body.error;
  } catch {
    // Response body wasn't JSON - use status text
  }

  return message;
}

async function getJson<T>(url: string): Promise<T> {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }

  return (await response.json()) as T;
}

/**
 * How many logs a job has. Zero is a normal answer, not an error - a job that
 * hasn't run yet has no log directory at all.
 */
export async function fetchJobLogCount(
  serverId: number,
  queueName: string,
  jobId: number,
): Promise<number> {
  const query = new URLSearchParams({ queue: queueName, job_id: String(jobId) });

  const body = await getJson<JobLogCountResponse>(
    `/admin/servers/${serverId}/jobs/log-count?${query}`,
  );

  // Validate at the boundary. A stale server build answers this route with a
  // different shape entirely, and an undefined count would otherwise flow on
  // into a request for log index "undefined".
  if (!Number.isInteger(body?.log_count) || body.log_count < 0) {
    throw new Error(`Unexpected log count response: ${JSON.stringify(body)}`);
  }

  return body.log_count;
}

/** The text of one of a job's logs. `index` is 1-based. */
export async function fetchJobLogData(
  serverId: number,
  queueName: string,
  jobId: number,
  index: number,
): Promise<string> {
  const query = new URLSearchParams({
    queue: queueName,
    job_id: String(jobId),
    index: String(index),
  });

  const body = await getJson<JobLogDataResponse>(
    `/admin/servers/${serverId}/jobs/log-data?${query}`,
  );

  if (typeof body?.data !== 'string') {
    throw new Error(`Unexpected log data response: ${JSON.stringify(body)}`);
  }

  return body.data;
}
